import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Clock, FileText, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API_BASE_URL } from '@/config/api';
import QuickSendHeader from '@/components/quick-send/QuickSendHeader';

interface TrackingData {
  title: string;
  status: string;
  recipient_name: string;
  is_expired: boolean;
  tracking_expires_at: string;
  created_at: string;
}

const statusConfig: Record<string, { icon: React.ElementType; color: string; bgColor: string }> = {
  pending: { icon: Clock, color: 'text-amber-600', bgColor: 'bg-amber-50' },
  completed: { icon: CheckCircle2, color: 'text-green-600', bgColor: 'bg-green-50' },
  signed: { icon: CheckCircle2, color: 'text-green-600', bgColor: 'bg-green-50' },
  declined: { icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-50' },
  partially_signed: { icon: Clock, color: 'text-blue-600', bgColor: 'bg-blue-50' },
};

const TrackDocument: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const { t } = useTranslation('quicksend');
  const [data, setData] = useState<TrackingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!code) return;

    const fetchTracking = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/guest/track/${code}`);
        if (response.status === 404) {
          setNotFound(true);
          return;
        }
        if (!response.ok) throw new Error('Failed to fetch');
        const result = await response.json();
        setData(result);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchTracking();
  }, [code]);

  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col">
      <QuickSendHeader />

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        {loading && (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {!loading && notFound && (
          <div className="max-w-md w-full text-center">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="h-7 w-7 text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">{t('track_not_found')}</h2>
            <p className="text-sm text-gray-500 mb-6">
              The tracking link may have expired or the document does not exist.
            </p>
            <div className="flex flex-col gap-2 items-center">
              <Button asChild>
                <Link to="/join">{t('track_create_account')}</Link>
              </Button>
              <span className="text-xs text-gray-400">
                {t('track_already_have_account')}{' '}
                <Link to="/login" className="text-primary hover:underline">{t('track_sign_in')}</Link>
              </span>
            </div>
          </div>
        )}

        {!loading && data && data.is_expired && (
          <div className="max-w-md w-full text-center">
            <div className="w-14 h-14 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="h-7 w-7 text-amber-500" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">{t('track_expired_title')}</h2>
            <p className="text-sm text-gray-500 mb-6">{t('track_expired_description')}</p>
            <div className="flex flex-col gap-2 items-center">
              <Button asChild>
                <Link to="/join">{t('track_create_account')}</Link>
              </Button>
              <span className="text-xs text-gray-400">
                {t('track_already_have_account')}{' '}
                <Link to="/login" className="text-primary hover:underline">{t('track_sign_in')}</Link>
              </span>
            </div>
          </div>
        )}

        {!loading && data && !data.is_expired && (
          <div className="max-w-md w-full">
            <div className="text-center mb-6">
              <h1 className="text-xl font-semibold text-gray-800 mb-1">{t('track_title')}</h1>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border">
              {/* Status badge */}
              {(() => {
                const config = statusConfig[data.status] || statusConfig.pending;
                const StatusIcon = config.icon;
                const statusKey = `track_status_${data.status}` as const;
                return (
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${config.bgColor} mb-5`}>
                    <StatusIcon className={`h-5 w-5 ${config.color}`} />
                    <span className={`text-sm font-medium ${config.color}`}>
                      {t(statusKey)}
                    </span>
                  </div>
                );
              })()}

              {/* Awaiting message */}
              {data.status === 'pending' && (
                <p className="text-lg font-medium text-gray-800 mb-5">
                  {t('track_awaiting', { name: data.recipient_name })}
                </p>
              )}

              {/* Details */}
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <FileText className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                  <div>
                    <span className="text-gray-500">{t('track_document_label')}</span>
                    <p className="font-medium text-gray-800">{data.title}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                  <div>
                    <span className="text-gray-500">{t('track_sent_label')}</span>
                    <p className="font-medium text-gray-800">{formatDate(data.created_at)}</p>
                  </div>
                </div>
              </div>

              <hr className="my-5" />

              {/* CTA */}
              <div className="text-center">
                <p className="text-xs text-gray-400 mb-3">
                  {t('success_tracking_description')}
                </p>
                <Button asChild className="w-full mb-2">
                  <Link to="/join">{t('success_tracking_cta')}</Link>
                </Button>
                <span className="text-xs text-gray-400">
                  {t('track_already_have_account')}{' '}
                  <Link to="/login" className="text-primary hover:underline">{t('track_sign_in')}</Link>
                </span>
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="text-center text-xs text-gray-400 py-4 border-t bg-white/50">
        © {new Date().getFullYear()} VivoSign
      </footer>
    </div>
  );
};

export default TrackDocument;
